import { APP_INITIALIZER, inject, Injectable, Provider } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import {
  filter,
  map,
  mergeWith,
  Observable,
  scan,
  shareReplay,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { Router, RoutesRecognized } from '@angular/router';

export enum VersionStatus {
  None,
  Detected,
  Ready,
  Unrecoverable,
  Failed,
}

const versionEventToStatus = {
  VERSION_DETECTED: VersionStatus.Detected,
  VERSION_INSTALLATION_FAILED: VersionStatus.Failed,
  VERSION_READY: VersionStatus.Ready,
  NO_NEW_VERSION_DETECTED: VersionStatus.None,
} as const satisfies Record<VersionEvent['type'], VersionStatus>;

@Injectable({ providedIn: 'root' })
export class VersionUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  version$: Observable<VersionStatus> = this.swUpdate.versionUpdates.pipe(
    map((event) => versionEventToStatus[event.type]),
    mergeWith(
      this.swUpdate.unrecoverable.pipe(map(() => VersionStatus.Unrecoverable)),
    ),
    scan(
      (acc, value) =>
        acc !== VersionStatus.None
          ? acc === VersionStatus.Detected
            ? VersionStatus.Ready
            : acc
          : value,
      VersionStatus.None,
    ),
    startWith(VersionStatus.None),
    shareReplay(1),
  );
}

export function provideVersionUpdates(): Provider[] {
  return [
    {
      provide: APP_INITIALIZER,
      useFactory: (versionSvc: VersionUpdateService, router: Router) => {
        // Initialize the app with a version update subscription that intercepts
        // navigation when an update is ready, and replaces the intercepted navigation
        // with a full application reload, to activate the update.
        return () =>
          router.events
            .pipe(
              withLatestFrom(versionSvc.version$),
              filter(
                (args): args is [RoutesRecognized, VersionStatus] =>
                  args[0] instanceof RoutesRecognized &&
                  [
                    VersionStatus.Failed,
                    VersionStatus.Ready,
                    VersionStatus.Unrecoverable,
                  ].includes(args[1]),
              ),
              switchMap(([event]) => {
                // Force reload the application to the navigation target page
                // via browser mechanism, reloads Angular with the installed version.
                document.location.href = event.url;
                // Replace the current navigation with a navigation to the current page,
                // effectively cancelling the ongoing navigation, and wait for the
                // browser page refresh to take us to the target page.
                return router.navigateByUrl(router.url, { replaceUrl: true });
              }),
            )
            .subscribe();
      },
      deps: [VersionUpdateService, Router],
      multi: true,
    },
  ];
}
